import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { L, t } from "../lib/translations";

const FEATURES = [
  {
    emoji: "🎁",
    bg: "rgba(225,48,108,0.10)",
    border: "rgba(225,48,108,0.18)",
    glow: "rgba(225,48,108,0.25)",
    title: { en: "Free followers & likes", ru: "Бесплатные подписчики", he: "עוקבים ולייקים בחינם" },
    desc: { en: "Complete tasks, earn iPoints, redeem for free products", ru: "Выполняйте задания, зарабатывайте iPoints, получайте продукты бесплатно", he: "בצע משימות, צבור iPoints, קבל מוצרים חינמיים" },
    action: "/tasks",
  },
  {
    emoji: "🛒",
    bg: "rgba(131,58,180,0.10)",
    border: "rgba(131,58,180,0.18)",
    glow: "rgba(131,58,180,0.25)",
    title: { en: "Buy Instagram services", ru: "Купить услуги", he: "רכוש שירותי אינסטגרם" },
    desc: { en: "Followers, likes & views — fast delivery", ru: "Подписчики, лайки и просмотры — быстрая доставка", he: "עוקבים, לייקים וצפיות — משלוח מהיר" },
    action: "/shop",
  },
  {
    emoji: "💎",
    bg: "rgba(64,93,230,0.10)",
    border: "rgba(64,93,230,0.18)",
    glow: "rgba(64,93,230,0.25)",
    title: { en: "Top up iPoints", ru: "Пополнить баланс", he: "הטען iPoints" },
    desc: { en: "Add iPoints instantly and use them for any service", ru: "Пополните iPoints мгновенно для любых услуг", he: "הוסף iPoints מיידית לכל שירות" },
    action: "/topup",
  },
  {
    emoji: "💬",
    bg: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.18)",
    glow: "rgba(16,185,129,0.25)",
    title: { en: "Support 24/7", ru: "Поддержка 24/7", he: "תמיכה 24/7" },
    desc: { en: "Have a question? Our team replies within minutes", ru: "Есть вопрос? Команда отвечает в течение нескольких минут", he: "יש שאלה? הצוות שלנו עונה תוך דקות" },
    action: "support",
  },
];

export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  if (!user) return null;

  const firstName = (user.name || user.email || "").split(/[\s@]/)[0];

  return (
    <div
      className="relative min-h-screen flex flex-col px-4 pt-24 pb-8"
      dir={isRtl ? "rtl" : "ltr"}
      style={{ overflowX: "hidden" }}
    >
      <style>{`
        @keyframes wUp   { from{opacity:0;transform:translateY(22px)}  to{opacity:1;transform:translateY(0)} }
        @keyframes wPop  { from{opacity:0;transform:scale(0.82)}       to{opacity:1;transform:scale(1)}     }
        @keyframes wGlow { 0%,100%{box-shadow:0 0 0 0 rgba(225,48,108,0.4),0 16px 40px rgba(225,48,108,0.25)}
                           55%    {box-shadow:0 0 0 9px rgba(225,48,108,0),0 16px 40px rgba(225,48,108,0.25)} }
        .wcard { animation: wUp 0.52s cubic-bezier(0.22,1,0.36,1) both; }
        .wcard:nth-child(1){animation-delay:.08s}
        .wcard:nth-child(2){animation-delay:.16s}
        .wcard:nth-child(3){animation-delay:.24s}
        .wcard:nth-child(4){animation-delay:.32s}
      `}</style>

      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div style={{ position:"absolute", top:"8%", left:"50%", transform:"translateX(-50%)", width:480, height:480, borderRadius:"50%", background:"radial-gradient(circle,rgba(225,48,108,0.11) 0%,transparent 70%)", filter:"blur(55px)" }} />
        <div style={{ position:"absolute", bottom:"8%", right:"5%", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(64,93,230,0.09) 0%,transparent 70%)", filter:"blur(45px)" }} />
      </div>

      <div className="relative max-w-lg mx-auto w-full flex flex-col gap-4">

        {/* ── Header ── */}
        <div className="text-center mb-2" style={{ animation:"wUp 0.48s ease both" }}>
          {user.picture && (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl mx-auto mb-3 object-cover"
              style={{ boxShadow:"0 8px 24px rgba(0,0,0,0.4)", border:"2px solid var(--surface-3)" }} />
          )}
          <h1 className="text-2xl font-black text-white tracking-tight mb-1"
            style={{ animation:"wPop 0.4s ease 0.06s both" }}>
            {L(lang, `Welcome, ${firstName}! 👋`, `Добро пожаловать, ${firstName}! 👋`, `!ברוך הבא, ${firstName} 👋`)}
          </h1>
          <p className="text-sm" style={{ color:"rgba(255,255,255,0.42)", animation:"wUp 0.45s ease 0.1s both" }}>
            {t("Welcome.here_s_everything_you_can_do_with_ifollowers", lang)}
          </p>
        </div>

        {/* ── Balance card ── */}
        <div className="rounded-2xl p-4 flex items-center gap-3"
          style={{ background:"var(--surface-2)", border:"1px solid var(--surface-3)", animation:"wUp 0.5s ease 0.06s both" }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-white text-sm"
            style={{ background:"linear-gradient(135deg,#F77737,#E1306C)", boxShadow:"0 8px 20px rgba(225,48,108,0.3)" }}>
            iP
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color:"rgba(255,255,255,0.35)" }}>
              {t("Dashboard.your_ipoints_balance", lang)}
            </div>
            <div className="text-xl font-black text-white leading-none">
              {(user.ipoints || 0).toLocaleString()} iPoints
            </div>
          </div>
          <button
            onClick={() => handleCard("/topup")}
            className="text-xs font-black px-3 py-1.5 rounded-full flex-shrink-0 transition-opacity hover:opacity-80"
            style={{ background:"linear-gradient(135deg,#F77737,#E1306C)", color:"#fff" }}
          >
            {t("Dashboard.top_up", lang)}
          </button>
        </div>

        {/* ── Feature cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURES.map((f, i) => (
            <button
              key={i}
              onClick={() => handleCard(f.action)}
              className="wcard text-start rounded-2xl p-4 transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{ background:f.bg, border:`1px solid ${f.border}` }}
            >
              <div className="text-2xl mb-2.5">{f.emoji}</div>
              <div className="font-bold text-sm text-white mb-1 leading-snug">{f.title[lang] || f.title.en}</div>
              <div className="text-xs leading-relaxed" style={{ color:"rgba(255,255,255,0.48)" }}>{f.desc[lang] || f.desc.en}</div>
            </button>
          ))}
        </div>

        {/* ── Coupon hint ── */}
        <div className="rounded-2xl p-3.5 flex items-center gap-3"
          style={{ background:"var(--surface-1)", border:"1px solid var(--surface-2)", animation:"wUp 0.5s ease 0.38s both" }}>
          <span className="text-xl flex-shrink-0">🎟️</span>
          <div className="min-w-0">
            <div className="text-xs font-bold text-white mb-0.5">
              {t("Welcome.got_a_coupon_code", lang)}
            </div>
            <div className="text-xs" style={{ color:"rgba(255,255,255,0.4)" }}>
              {t("Welcome.enter_it_in_your_dashboard_rewards_tab_for_bonus", lang)}
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <button
          onClick={done}
          className="w-full py-4 rounded-2xl text-white font-black text-base transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ background:"linear-gradient(135deg,#E1306C,#833AB4)", boxShadow:"0 10px 28px rgba(225,48,108,0.35)", animation:"wGlow 3.5s ease-in-out infinite, wUp 0.5s ease 0.44s both" }}
        >
          {t("Dashboard.let_s_go", lang)}
        </button>
      </div>
    </div>
  );
}
